import { uiText, useUiLanguage } from '../../../../../utils/uiTranslation';
import React, { useEffect, useRef, useState } from 'react';

const SIZE_ORDER = ['compact', 'medium', 'three-quarter', 'large'];

const clampSize = (currentSize, delta) => {
  const index = SIZE_ORDER.indexOf(currentSize);
  const nextIndex = Math.max(0, Math.min(SIZE_ORDER.length - 1, index + delta));
  return SIZE_ORDER[nextIndex];
};

const WidgetShell = ({
  id,
  title,
  annotationItems = [],
  className = '',
  size = 'large',
  pairedCompact = false,
  variant = 'data-forward',
  chartTypes = [],
  activeChartType,
  groupByOptions = [],
  activeGroupBy = '',
  menuCopy,
  headerActions = [],
  helpText = '',
  onChartTypeChange,
  onGroupByChange,
  onSizeChange,
  allowResize = true,
  children,
  isRTL = false
}) => {
  useUiLanguage();
  const [menuOpen, setMenuOpen] = useState(false);
  const [dragging, setDragging] = useState(false);
  const shellRef = useRef(null);
  const dragStateRef = useRef(null);
  const previousSizeRef = useRef(size);

  useEffect(() => {
    const handlePointerDown = (event) => {
      if (shellRef.current && !shellRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, []);

  useEffect(() => {
    if (previousSizeRef.current !== size && shellRef.current?.animate) {
      shellRef.current.animate(
        [
          { opacity: 0.84, transform: 'scale(0.985)' },
          { opacity: 1, transform: 'scale(1)' }
        ],
        { duration: 220, easing: 'ease' }
      );
    }
    previousSizeRef.current = size;
  }, [size]);

  useEffect(() => {
    const handlePointerMove = (event) => {
      if (!dragStateRef.current) return;
      const dx = event.clientX - dragStateRef.current.startX;
      if (Math.abs(dx) > 18) {
        setDragging(true);
      }
    };

    const handlePointerUp = (event) => {
      if (!dragStateRef.current) return;
      const dx = event.clientX - dragStateRef.current.startX;
      const direction = dx > 70 ? 1 : dx < -70 ? -1 : 0;

      if (direction !== 0 && typeof onSizeChange === 'function') {
        onSizeChange(id, clampSize(size, direction));
      }

      dragStateRef.current = null;
      setDragging(false);
    };

    window.addEventListener('mousemove', handlePointerMove);
    window.addEventListener('mouseup', handlePointerUp);

    return () => {
      window.removeEventListener('mousemove', handlePointerMove);
      window.removeEventListener('mouseup', handlePointerUp);
    };
  }, [id, onSizeChange, size]);

  const normalizedChartTypes = chartTypes.map((type) =>
    typeof type === 'string' ? { id: type, label: type } : type
  );
  const showChartTypeMenu = normalizedChartTypes.length > 1;
  const showGroupByMenu = groupByOptions.length > 1;
  const showSizeMenu = Boolean(allowResize);
  const hasSettingsMenu = showChartTypeMenu || showGroupByMenu || showSizeMenu;

  return (
    <article
      ref={shellRef}
      className={`executive-overview-panel analytics-recruitment-widget analytics-recruitment-widget-${size} analytics-recruitment-widget-${variant}${pairedCompact ? ' analytics-recruitment-widget-paired-compact' : ''}${dragging ? ' dragging' : ''} ${className}`.trim()}
    >
      <div className="analytics-recruitment-widget-hover-controls">
        {hasSettingsMenu ? (
          <div className={`analytics-recruitment-widget-menu${menuOpen ? ' open' : ''}${isRTL ? ' rtl' : ''}`}>
            {showChartTypeMenu ? (
              <div className="analytics-recruitment-widget-menu-group">
                <div className="analytics-recruitment-widget-menu-label">{menuCopy.chartType}</div>
                {normalizedChartTypes.map((type) => (
                  <button
                    key={type.id}
                    type="button"
                    className={`analytics-recruitment-widget-menu-item${activeChartType === type.id ? ' active' : ''}`}
                    onClick={() => {
                      onChartTypeChange?.(id, type.id);
                      setMenuOpen(false);
                    }}
                  >
                    {uiText(type.label)}
                  </button>
                ))}
              </div>
            ) : null}

            {showGroupByMenu ? (
              <div className="analytics-recruitment-widget-menu-group">
                <div className="analytics-recruitment-widget-menu-label">{menuCopy.groupBy}</div>
                {groupByOptions.map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    className={`analytics-recruitment-widget-menu-item${activeGroupBy === option.id ? ' active' : ''}`}
                    onClick={() => {
                      onGroupByChange?.(id, option.id);
                      setMenuOpen(false);
                    }}
                  >
                    {uiText(option.label)}
                  </button>
                ))}
              </div>
            ) : null}

            {showSizeMenu ? (
              <div className="analytics-recruitment-widget-menu-group">
                <div className="analytics-recruitment-widget-menu-label">{menuCopy.cardSize}</div>
                {SIZE_ORDER.map((option) => (
                  <button
                    key={option}
                    type="button"
                    className={`analytics-recruitment-widget-menu-item${size === option ? ' active' : ''}`}
                    onClick={() => {
                      onSizeChange?.(id, option);
                      setMenuOpen(false);
                    }}
                  >
                    {menuCopy[option]}
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        ) : null}

        {allowResize ? (
          <button
            type="button"
            className="analytics-recruitment-widget-resize-handle"
            aria-label={menuCopy.resize}
            onMouseDown={(event) => {
              dragStateRef.current = { startX: event.clientX };
              setDragging(false);
              event.preventDefault();
            }}
          />
        ) : null}
      </div>

      <div className="analytics-recruitment-widget-header">
        <div className="analytics-recruitment-widget-title-row">
          <div className="analytics-recruitment-widget-title-wrap">
            <div className="executive-overview-card-title">{title}</div>
            {helpText ? (
              <span className={`analytics-recruitment-help-wrap${isRTL ? ' rtl' : ''}`}>
                <button
                  type="button"
                  className="analytics-recruitment-help-trigger"
                  aria-label={helpText}
                >
                  ?
                </button>
                <span className="analytics-recruitment-help-tooltip" role="tooltip">{helpText}</span>
              </span>
            ) : null}
          </div>
          {headerActions.length || hasSettingsMenu ? (
            <div className="analytics-recruitment-widget-header-actions">
              {headerActions.map((action) => (
                <button
                  key={action.id}
                  type="button"
                  className="analytics-recruitment-widget-header-action"
                  onClick={() => action.onClick?.(id)}
                >
                  {action.icon ? <i className={`fa-solid ${action.icon}`} /> : null}
                  <span>{uiText(action.label)}</span>
                </button>
              ))}
              {hasSettingsMenu ? (
                <button
                  type="button"
                  className="analytics-recruitment-widget-header-action analytics-recruitment-widget-settings-inline"
                  aria-label={menuCopy.settings}
                  onClick={() => setMenuOpen((prev) => !prev)}
                >
                  <i className="fa-solid fa-gear" />
                </button>
              ) : null}
            </div>
          ) : null}
        </div>

        {annotationItems.length ? (
          <div className={`analytics-recruitment-annotation-line${isRTL ? ' rtl' : ''}`}>
            {annotationItems.map((item, index) => (
              <React.Fragment key={item}>
                {index > 0 ? <span className="analytics-recruitment-annotation-dot" aria-hidden="true" /> : null}
                <span className="analytics-recruitment-annotation-item">{item}</span>
              </React.Fragment>
            ))}
          </div>
        ) : null}
      </div>

      <div className="analytics-recruitment-widget-body">{children}</div>
    </article>
  );
};

export default WidgetShell;
